import { useCallback, useEffect, useRef, useState } from 'react';

export interface PortalDropdownCoords {
  top: number;
  right: number;
}

/**
 * Mechanics for a portaled dropdown: open state, one-time position measurement
 * (anchored to the trigger's bottom-right), and outside-click / scroll / resize
 * dismissal. Rendering is left to the consumer (see `PortalDropdown`), so each
 * menu keeps its own trigger and panel content.
 *
 * The dropdown is meant to be portaled to <body> with fixed positioning so it
 * is never clipped by an ancestor's overflow / scroll container.
 */
export function usePortalDropdown() {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PortalDropdownCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(() => {
    if (open) {
      close();
      return;
    }
    const el = triggerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(true);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      close();
    };
    // Fixed position is a one-time measurement, so any scroll/resize would
    // detach the panel from the trigger - close instead of trying to follow.
    document.addEventListener('mousedown', handlePointer);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return { open, coords, triggerRef, dropdownRef, toggle, close };
}
